import { memo, type ReactNode } from 'react';

export type WorkspaceAgentPreviewCardMetaRowProps = {
  children: ReactNode;
  /**
   * Already `aria-hidden` at the call site: the row's own text names what the glyph stands for. Optional
   * because some rows genuinely have no glyph — inventing one the prototype does not draw would be worse.
   */
  icon?: ReactNode;
  testId?: string;
  title?: string;
};

export const WorkspaceAgentPreviewCardMetaRow = memo(
  ({ children, icon, testId, title }: WorkspaceAgentPreviewCardMetaRowProps) => {
    /**
     * `title` on a generic span is neither the accessible name nor the description, so a middle-collapsed
     * trail would lose its middle crumbs for a screen reader with no equivalent anywhere else.
     */
    const hasCollapsedText = !!title && typeof children === 'string' && title !== children;

    return (
      // No `text-11` token, so the prototype's 11px is bracketed; `gray-10`, not the prototype's `gray-9`, to clear AA at that size.
      <span
        className="flex min-w-0 items-start gap-1 ads:text-metadata text-[11px] text-gray-10 leading-4"
        data-testid={testId}
      >
        {icon ? <span className="mt-0.5 flex shrink-0 items-center">{icon}</span> : null}
        <span className="min-w-0 break-words" title={title}>
          {hasCollapsedText ? (
            <>
              <span className="sr-only">{title}</span>
              <span aria-hidden>{children}</span>
            </>
          ) : (
            children
          )}
        </span>
      </span>
    );
  },
);

WorkspaceAgentPreviewCardMetaRow.displayName = 'WorkspaceAgentPreviewCardMetaRow';
